import React, { useMemo, useState } from "react";
import Select, { components } from "react-select";
import { Controller } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import { getNameProfile } from "Utils/utils";
import { IMAGE_URL } from "store/services/URL";

const resolveNameParts = (data) => {
  const first = data?.first_name?.trim() || "";
  const last = data?.last_name?.trim() || "";
  if (first && last) return { first, last };

  const parts = String(data?.label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return { first: parts[0], last: parts[parts.length - 1] };
  }
  if (parts.length === 1) {
    return { first: parts[0], last: parts[0] };
  }
  return { first: "?", last: "?" };
};

const getAvatarSrc = (data) => {
  if (data?.icon) {
    const icon = String(data.icon).trim();
    if (icon.startsWith("http")) return icon;
    return `${IMAGE_URL}${icon}`;
  }

  const photo = data?.photo ? String(data.photo).trim() : "";
  if (photo !== "") {
    return photo.startsWith("http") ? photo : `${IMAGE_URL}${photo}`;
  }

  const pic = data?.profile_picture ? String(data.profile_picture).trim() : "";
  if (pic !== "") {
    return pic.startsWith("http") ? pic : `${IMAGE_URL}${pic}`;
  }

  const { first, last } = resolveNameParts(data);
  return getNameProfile(first, last) || getNameProfile("?", "?");
};

const MultiSelectDropdown = ({
  data,
  cssClass,
  control,
  showIcon = false,
  setValue,
  name,
  watch,
  clearErrors,
  errorMessage,
  rules,
  selectedOptions,
  isDisabled,
  calendarView,
  setSelectedOptions,
  watchFormData,
  selectedShift = "",
  handleInputChange,
  menuPortalZIndex = 100000,
  minSelectionCount = 0,
  onMinSelectionBlocked,
  /** Controlled mode (no RHF) — same UI as group-shift worker multi-select */
  value: controlledValue,
  onChange: controlledOnChange,
  isClearable: isClearableProp,
  /** Match compact text inputs (e.g. advance-mode task field height) */
  compact = false,
  /**
   * When true, close the portaled menu if the page/modal scrolls.
   * Scrolling inside the options list stays open. Opt-in so other screens are unchanged.
   */
  closeMenuOnPageScroll = false,
  /** Optional override. When provided, used instead of closeMenuOnPageScroll. */
  closeMenuOnScroll: closeMenuOnScrollProp,
  /** Optional react-select menuPosition. Omitted unless the caller passes it. */
  menuPosition,
  onMenuOpen,
}) => {
  const isControlled =
    typeof controlledOnChange === "function" && typeof watch !== "function";

  const handleCloseMenuOnScroll =
    typeof closeMenuOnScrollProp === "function"
      ? closeMenuOnScrollProp
      : closeMenuOnPageScroll
        ? (event) => {
            const target = event?.target;
            if (
              target instanceof Element &&
              target.closest(".multi-select-dropdown__menu")
            ) {
              return false;
            }
            return true;
          }
        : undefined;

  const menuPositionProps = menuPosition ? { menuPosition } : {};

  const values = isControlled ? controlledValue : watch?.(name);

  const mapToSelectOption = (item) => {
    if (item == null) return null;
    if (typeof item === "string" || typeof item === "number") {
      const found = data?.find(
        (d) => d.uuid === item || d.value === item || String(d.id) === String(item)
      );
      if (found) {
        return {
          ...found,
          label:
            found.label ||
            `${found.first_name || ""} ${found.last_name || ""}`.trim(),
          value: found.uuid || found.value,
        };
      }
      return null;
    }

    // Enrich draft/partial selections from the loaded options list (match by uuid or id)
    const found = data?.find(
      (d) =>
        (item?.uuid && (d.uuid === item.uuid || d.value === item.uuid)) ||
        (item?.value && (d.uuid === item.value || d.value === item.value)) ||
        (item?.id != null && String(d.id) === String(item.id))
    );
    const source = found || item;
    const label =
      source.label ||
      item.label ||
      `${source.first_name || item.first_name || ""} ${
        source.last_name || item.last_name || ""
      }`.trim();
    const { first, last } = resolveNameParts({ ...item, ...source, label });
    return {
      ...item,
      ...source,
      email: source.email ?? item.email,
      first_name: source.first_name || item.first_name || first,
      icon: source.icon || item.icon || "",
      id: source.id ?? item.id,
      label: label || "Participant",
      last_name: source.last_name || item?.last_name || last,
      profile_picture: source.profile_picture || item?.profile_picture || "",
      photo: source.photo || item?.photo || "",
      uuid: source.uuid || source.value || item?.uuid || item?.value,
      value: source.uuid || source.value || item?.uuid || item?.value,
    };
  };

  const normalizedMultiValue = useMemo(() => {
    if (values == null || values === undefined) return [];
    if (Array.isArray(values)) {
      return values.map((item) => mapToSelectOption(item)).filter(Boolean);
    }
    const single = mapToSelectOption(values);
    return single ? [single] : [];
  }, [values, data]);

  const defaultValues = calendarView
    ? normalizedMultiValue
    : data?.find((item) => item.uuid === values || item.value === values);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const CheckboxOption = ({ innerProps, label, isSelected }) => (
    <div
      {...innerProps}
      className={`flex items-center justify-between mt-1 p-2 ${
        isSelected ? "bg-[#516CB8] " : "bg-white "
      }`}
    >
      <label
        className={`ml-2 block text-md capitalize ${
          isSelected ? "text-white" : "text-gray-900"
        }`}
      >
        {label}
      </label>
      <div
        className={`mx-2 h-6 w-6 rounded-full flex items-center cursor-pointer justify-center border ${
          isSelected ? "bg-white" : ""
        }`}
        onClick={() => handleOptionToggle(label)}
      >
        {isSelected && <FaCheck className='bg-white text-[#516cb8]' />}
      </div>
    </div>
  );

  const handleImageError = (e, data) => {
    const { first, last } = resolveNameParts(data);
    e.target.src = getNameProfile(first, last, "0D8ABC") || getNameProfile("?", "?");
  };

  const CustomSelectValue = (csprops) => {
    const data = csprops.data;
    return (
      <components.SingleValue {...csprops}>
        <div className='flex items-center capitalize'>
          {data?.label && (
            <img
              onError={(e) => handleImageError(e, data)}
              className='w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2'
              src={getAvatarSrc(data)}
              alt=''
            />
          )}
          {data?.label}
        </div>
      </components.SingleValue>
    );
  };

  const CustomMultiValueLabel = (props) => {
    const { data } = props;
    return (
      <components.MultiValueLabel {...props}>
        <div className='flex items-center capitalize'>
          {data?.label && (
            <img
              onError={(e) => handleImageError(e, data)}
              className={`${
                compact ? "w-4 h-4" : "w-5 h-5"
              } bg-gray-300 rounded-full flex-shrink-0 object-cover mr-1`}
              src={getAvatarSrc(data)}
              alt=''
            />
          )}
          <span className='truncate'>{data?.label}</span>
        </div>
      </components.MultiValueLabel>
    );
  };

  const CustomOption = (props) => {
    const { data } = props;
    return (
      <components.Option {...props}>
        <div className='flex items-center capitalize'>
          {data?.label && (
            <img
              className='w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2'
              src={getAvatarSrc(data)}
              alt=''
              onError={(e) => handleImageError(e, data)}
            />
          )}
          {data?.label}
        </div>
      </components.Option>
    );
  };
  const handleOptionToggle = (optionLabel) => {
    const selectedOption = data.find((option) => option.label === optionLabel);
    if (!selectedOption) return;

    const selectedOptions = watch(name) || [];
    const isSelected = selectedOptions.some(
      (opt) => opt.value === selectedOption.value
    );

    if (isSelected) {
      setValue(
        name,
        selectedOptions.filter((opt) => opt.value !== selectedOption.value)
      );
    } else {
      setValue(name, [...selectedOptions, selectedOption]);
    }
  };

  const customStyles = {
    multiValueRemove: (base, state) => ({
      ...base,
      display: "block",
      ...(compact
        ? {
            paddingLeft: "2px",
            paddingRight: "4px",
          }
        : {}),
    }),
    menuPortal: (base) => ({ ...base, zIndex: menuPortalZIndex }),
    menuList: (base) => ({
      ...base,
      maxHeight: "200px !important",
      overflowY: "auto !important",
      scrollbarWidth: "thin",
    }),
    valueContainer: (base) => ({
      ...base,
      display: "flex",
      flexWrap: "wrap",
      alignItems: compact ? "center" : "flex-start",
      maxHeight: "100%",
      overflowY: "auto",
      ...(compact
        ? {
            paddingTop: "2px",
            paddingBottom: "2px",
            paddingLeft: "8px",
            minHeight: "36px",
          }
        : {}),
    }),
    multiValue: (base) => ({
      ...base,
      margin: compact ? "1px 2px" : "2px",
      ...(compact
        ? {
            minHeight: "24px",
            alignItems: "center",
          }
        : {}),
    }),
    multiValueLabel: (base) => ({
      ...base,
      ...(compact
        ? {
            paddingTop: "1px",
            paddingBottom: "1px",
            paddingLeft: "4px",
            fontSize: "12px",
          }
        : {}),
    }),
    indicatorsContainer: (base) => ({
      ...base,
      ...(compact
        ? {
            alignItems: "center",
            height: "36px",
          }
        : {}),
    }),
    dropdownIndicator: (base) => ({
      ...base,
      ...(compact
        ? {
            paddingTop: "6px",
            paddingBottom: "6px",
          }
        : {}),
    }),
    clearIndicator: (base) => ({
      ...base,
      ...(compact
        ? {
            paddingTop: "6px",
            paddingBottom: "6px",
          }
        : {}),
    }),
    input: (base) => ({
      ...base,
      ...(compact
        ? {
            margin: "0",
            paddingTop: "0",
            paddingBottom: "0",
          }
        : {}),
    }),
    control: (base) => ({
      ...base,
      minHeight: compact ? "38px" : "40px",
      height: compact ? "auto" : undefined,
      alignItems: compact ? "center" : "flex-start",
      ...(compact
        ? {
            borderRadius: "0.375rem",
          }
        : {}),
    }),
  };

  // console.log("defaultValues", defaultValues);
  // console.log(
  //   "sfsfsd",
  //   calendarView
  //     ? defaultValues !== undefined
  //       ? defaultValues
  //       : null
  //     : watch(name)
  // );

  const selectValue = isControlled
    ? normalizedMultiValue
    : calendarView
    ? normalizedMultiValue
    : watch?.(name);

  const handleSelectChange = (c) => {
    const next = Array.isArray(c) ? c : [];
    if (minSelectionCount > 0 && next.length < minSelectionCount) {
      onMinSelectionBlocked?.();
      return;
    }

    if (handleInputChange) {
      handleInputChange(next);
    }

    if (isControlled) {
      controlledOnChange(next);
      return;
    }

    setValue?.(name, next);
    clearErrors?.(name, "");
  };

  const allowClear =
    isClearableProp !== undefined ? isClearableProp : !minSelectionCount;

  if (isControlled && showIcon) {
    return (
      <>
        <Select
          isMulti
          isDisabled={isDisabled}
          options={data}
          value={selectValue}
          closeMenuOnSelect={false}
          components={{
            Option: CustomOption,
            SingleValue: CustomSelectValue,
            MultiValueLabel: CustomMultiValueLabel,
          }}
          isClearable={allowClear}
          clearableValue={allowClear}
          onChange={handleSelectChange}
          styles={customStyles}
          menuPortalTarget={document.body}
          classNamePrefix='multi-select-dropdown'
          closeMenuOnScroll={handleCloseMenuOnScroll}
          onMenuOpen={onMenuOpen}
          {...menuPositionProps}
          className={`basic-multi-select w-100 roleSelector scrollOptions ${cssClass}`}
          placeholder='Choose worker'
        />
        {errorMessage && <p className='text-red-500'>{errorMessage}</p>}
      </>
    );
  }

  return (
    <>
      {showIcon ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValues || []}
          rules={rules}
          render={({ field }) => (
            <>
              <Select
                isMulti
                isDisabled={isDisabled}
                options={data}
                value={selectValue}
                closeMenuOnSelect={false}
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSelectValue,
                  MultiValueLabel: CustomMultiValueLabel,
                }}
                isClearable={allowClear}
                clearableValue={allowClear}
                onChange={handleSelectChange}
                styles={customStyles}
                menuPortalTarget={document.body}
                classNamePrefix='multi-select-dropdown'
                closeMenuOnScroll={handleCloseMenuOnScroll}
                onMenuOpen={onMenuOpen}
                {...menuPositionProps}
                className={`basic-multi-select w-100 roleSelector scrollOptions ${cssClass}`}
              />
              {errorMessage && <p className='text-red-500'>{errorMessage}</p>}
            </>
          )}
        />
      ) : (
        <Select
          isMulti
          options={data}
          value={selectedOptions}
          components={{ Option: CheckboxOption }}
          menuIsOpen={menuIsOpen}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          onMenuBlur={() => setMenuIsOpen(false)}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isClearable={false}
          clearableValue={false}
          styles={customStyles}
          className={`basic-multi-select w-100 roleSelector scrollOptions ${cssClass}`}
        />
      )}
    </>
  );
};

export default MultiSelectDropdown;
